import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import type {
  CalidadSuministradorHomologadoDirecto,
  CalidadSuministradorHomologadoGrupo,
  CalidadSuministradorHomologadoIndirecto,
} from "../types/suministradores-homologados";

type Props =
  | {
      kind: "indirecto";
      title: string;
      rows: CalidadSuministradorHomologadoIndirecto[];
      loading: boolean;
      error: string | null;
      onRetry: () => void;
      onCreate: () => void;
      onEdit: (row: CalidadSuministradorHomologadoIndirecto) => void;
      onDelete: (row: CalidadSuministradorHomologadoIndirecto) => void;
    }
  | {
      kind: "directo" | "grupo";
      title: string;
      rows:
        | CalidadSuministradorHomologadoDirecto[]
        | CalidadSuministradorHomologadoGrupo[];
      loading: boolean;
      error: string | null;
      onRetry: () => void;
      onCreate: () => void;
      onEdit: (
        row:
          | CalidadSuministradorHomologadoDirecto
          | CalidadSuministradorHomologadoGrupo,
      ) => void;
      onDelete: (
        row:
          | CalidadSuministradorHomologadoDirecto
          | CalidadSuministradorHomologadoGrupo,
      ) => void;
    };

const PAGE_SIZE = 20;

export function CalidadSuministradoresBlock(props: Props) {
  const [page, setPage] = useState(1);
  const total = props.rows.length;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = total === 0 ? 0 : Math.min(total, page * PAGE_SIZE);

  const pageRows = useMemo(
    () => props.rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, props.rows],
  );

  useEffect(() => {
    setPage((current) =>
      Math.min(current, Math.max(1, Math.ceil(props.rows.length / PAGE_SIZE))),
    );
  }, [props.rows.length]);

  return (
    <section className="calidad-suppliers-block">
      <div className="calidad-suppliers-block-header">
        <div>
          <div className="calidad-toolbar-eyebrow">Revision rapida</div>
          <h3 className="calidad-toolbar-title">{props.title}</h3>
        </div>

        <div className="calidad-toolbar-meta">
          <span className="calidad-toolbar-count">
            {props.loading ? "Cargando..." : `${total} registros`}
          </span>

          <button
            type="button"
            onClick={props.onCreate}
            className="calidad-primary-button"
          >
            <Plus size={16} />
            Nuevo
          </button>
        </div>
      </div>

      {props.error ? (
        <div className="calidad-feedback-state calidad-feedback-state-compact">
          <div className="calidad-feedback-title">No se pudo cargar la tabla</div>
          <p className="calidad-feedback-copy">{props.error}</p>
          <button
            type="button"
            onClick={props.onRetry}
            className="calidad-feedback-action"
          >
            <RefreshCw size={15} />
            Reintentar
          </button>
        </div>
      ) : props.loading ? (
        <div className="calidad-feedback-state calidad-feedback-state-compact">
          <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
          <div className="calidad-feedback-title">Cargando registros</div>
          <p className="calidad-feedback-copy">
            Estamos recuperando los suministradores homologados.
          </p>
        </div>
      ) : props.rows.length === 0 ? (
        <div className="calidad-feedback-state calidad-feedback-state-compact">
          <div className="calidad-feedback-title">No hay registros</div>
          <p className="calidad-feedback-copy">
            Crea el primer registro para este bloque de suministradores.
          </p>
          <button
            type="button"
            onClick={props.onCreate}
            className="calidad-primary-button"
          >
            <Plus size={16} />
            Nuevo
          </button>
        </div>
      ) : (
        <>
          <div className="calidad-suppliers-table-scroll">
            <div className="calidad-suppliers-table-wrap">
              {props.kind === "indirecto" ? (
                <table className="calidad-table calidad-suppliers-matrix-table min-w-full">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="calidad-suppliers-actions-col">
                        Acciones
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-name-col">
                        Nombre
                      </th>
                      <th colSpan={4} className="calidad-suppliers-group-heading calidad-suppliers-group-end">
                        Material / Servicio
                      </th>
                      <th colSpan={6} className="calidad-suppliers-group-heading calidad-suppliers-group-end">
                        Tipologia
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-auth-col calidad-suppliers-group-end">
                        Autorizacion
                      </th>
                      <th colSpan={2} className="calidad-suppliers-group-heading calidad-suppliers-group-end">
                        Uso web
                      </th>
                      <th colSpan={5} className="calidad-suppliers-group-heading">
                        Nivel confianza
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-observaciones-col">
                        Observaciones / interes
                      </th>
                    </tr>
                    <tr>
                      <th className="calidad-suppliers-compact-col">M</th>
                      <th className="calidad-suppliers-compact-col">T</th>
                      <th className="calidad-suppliers-compact-col">G</th>
                      <th className="calidad-suppliers-compact-col calidad-suppliers-group-end">A</th>

                      <th className="calidad-suppliers-compact-col">DIR</th>
                      <th className="calidad-suppliers-compact-col">IND</th>
                      <th className="calidad-suppliers-compact-col">PUN</th>
                      <th className="calidad-suppliers-compact-col">CON</th>
                      <th className="calidad-suppliers-compact-col">NAC</th>
                      <th className="calidad-suppliers-compact-col calidad-suppliers-group-end">LOC</th>

                      <th className="calidad-suppliers-compact-col">PLAN</th>
                      <th className="calidad-suppliers-compact-col calidad-suppliers-group-end">DOC</th>

                      <th className="calidad-suppliers-compact-col">CA</th>
                      <th className="calidad-suppliers-compact-col">CM</th>
                      <th className="calidad-suppliers-compact-col">CB</th>
                      <th className="calidad-suppliers-compact-col">PC</th>
                      <th className="calidad-suppliers-compact-col">NC</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(pageRows as CalidadSuministradorHomologadoIndirecto[]).map(
                      (row) => (
                        <tr key={row.id}>
                          <td className="calidad-suppliers-actions-col">
                            <div className="calidad-table-actions-row">
                              <button
                                type="button"
                                onClick={() => props.onEdit(row)}
                                title="Editar indirecto"
                                aria-label="Editar indirecto"
                                className="calidad-icon-button"
                              >
                                <Pencil size={15} />
                              </button>

                              <button
                                type="button"
                                onClick={() => props.onDelete(row)}
                                title="Eliminar indirecto"
                                aria-label="Eliminar indirecto"
                                className="calidad-icon-button danger"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>

                          <td className="calidad-suppliers-name-col">{row.nombre}</td>

                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.material_servicio_m)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.material_servicio_t)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.material_servicio_g)}
                          </td>
                          <td className="calidad-suppliers-compact-col calidad-suppliers-group-end">
                            {renderMarkCell(row.material_servicio_a)}
                          </td>

                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.tipologia_dir)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.tipologia_ind)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.tipologia_pun)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.tipologia_con)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.tipologia_nac)}
                          </td>
                          <td className="calidad-suppliers-compact-col calidad-suppliers-group-end">
                            {renderMarkCell(row.tipologia_loc)}
                          </td>

                          <td className="calidad-suppliers-auth-col calidad-suppliers-group-end">
                            {renderMarkCell(row.autorizacion)}
                          </td>

                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.uso_web_plan)}
                          </td>
                          <td className="calidad-suppliers-compact-col calidad-suppliers-group-end">
                            {renderMarkCell(row.uso_web_doc)}
                          </td>

                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.nivel_confianza_ca)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.nivel_confianza_cm)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.nivel_confianza_cb)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.nivel_confianza_pc)}
                          </td>
                          <td className="calidad-suppliers-compact-col">
                            {renderMarkCell(row.nivel_confianza_nc)}
                          </td>

                          <td className="calidad-suppliers-observaciones-col">
                            {renderOptionalText(row.observaciones_interes)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="calidad-table calidad-suppliers-matrix-table min-w-full">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="calidad-suppliers-actions-col">
                        Acciones
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-name-col">
                        Nombre
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-service-col">
                        Servicio
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-comments-col">
                        Comentarios
                      </th>
                      <th colSpan={5} className="calidad-suppliers-group-heading">
                        Nivel confianza
                      </th>
                      <th rowSpan={2} className="calidad-suppliers-observaciones-col">
                        Observaciones / interes
                      </th>
                    </tr>
                    <tr>
                      <th className="calidad-suppliers-compact-col">CA</th>
                      <th className="calidad-suppliers-compact-col">CM</th>
                      <th className="calidad-suppliers-compact-col">CB</th>
                      <th className="calidad-suppliers-compact-col">PC</th>
                      <th className="calidad-suppliers-compact-col">NC</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(
                      pageRows as Array<
                        | CalidadSuministradorHomologadoDirecto
                        | CalidadSuministradorHomologadoGrupo
                      >
                    ).map((row) => (
                      <tr key={row.id}>
                        <td className="calidad-suppliers-actions-col">
                          <div className="calidad-table-actions-row">
                            <button
                              type="button"
                              onClick={() => props.onEdit(row)}
                              title={`Editar ${props.kind}`}
                              aria-label={`Editar ${props.kind}`}
                              className="calidad-icon-button"
                            >
                              <Pencil size={15} />
                            </button>

                            <button
                              type="button"
                              onClick={() => props.onDelete(row)}
                              title={`Eliminar ${props.kind}`}
                              aria-label={`Eliminar ${props.kind}`}
                              className="calidad-icon-button danger"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>

                        <td className="calidad-suppliers-name-col">{row.nombre}</td>
                        <td className="calidad-suppliers-service-col">
                          {renderOptionalText(row.servicio)}
                        </td>
                        <td className="calidad-suppliers-comments-col">
                          {renderOptionalText(row.comentarios)}
                        </td>

                        <td className="calidad-suppliers-compact-col">
                          {renderMarkCell(row.nivel_confianza_ca)}
                        </td>
                        <td className="calidad-suppliers-compact-col">
                          {renderMarkCell(row.nivel_confianza_cm)}
                        </td>
                        <td className="calidad-suppliers-compact-col">
                          {renderMarkCell(row.nivel_confianza_cb)}
                        </td>
                        <td className="calidad-suppliers-compact-col">
                          {renderMarkCell(row.nivel_confianza_pc)}
                        </td>
                        <td className="calidad-suppliers-compact-col">
                          {renderMarkCell(row.nivel_confianza_nc)}
                        </td>

                        <td className="calidad-suppliers-observaciones-col">
                          {renderOptionalText(row.observaciones_interes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="calidad-pagination">
            <div className="calidad-pagination-meta">
              {`Mostrando ${from}-${to} de ${total}`}
            </div>

            <div className="calidad-pagination-controls">
              <button
                type="button"
                onClick={() => setPage((current) => current - 1)}
                disabled={page <= 1}
                className="calidad-pagination-button"
              >
                Anterior
              </button>

              <span className="calidad-pagination-page">
                {`Pagina ${page} de ${lastPage}`}
              </span>

              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={page >= lastPage}
                className="calidad-pagination-button"
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function renderMarkCell(value: string | null) {
  const mark = value?.trim();

  if (!mark) {
    return <span className="calidad-suppliers-empty-cell" aria-hidden="true" />;
  }

  return (
    <span className="calidad-suppliers-mark-cell" title={mark} aria-label={mark}>
      {mark}
    </span>
  );
}

function renderOptionalText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return <div className="calidad-suppliers-text-cell">{value}</div>;
}
